import React from 'react';
import { useTranslation } from 'react-i18next';
import VerifiedBadge from './VerifiedBadge';

/**
 * UserDisplay Component - ALWAYS USE THIS for displaying user names.
 *
 * Usage:
 * <UserDisplay user={userData} />
 * <UserDisplay user={userData} size="sm" />
 * <UserDisplay user={userData} showUsername />
 */
const UserDisplay = ({
  user,
  size = 'md',
  showUsername = false,
  showBadge = true,
  className = '',
  nameClassName = '',
  onClick = null,
}) => {
  const { t } = useTranslation('common');
  if (!user) {
    return <span className={`font-medium ${className}`}>{t('unknownUser', 'Unknown User')}</span>;
  }

  const displayInfo = user.user_display_info || {};
  const isVerified = displayInfo.is_verified ?? user.is_verified ?? user.verified ?? false;
  const isCreator = displayInfo.is_creator ?? (user.user_type === 'creator') ?? isVerified;
  const isAdmin = displayInfo.is_admin ?? user.is_admin ?? (user.user_type === 'admin') ?? false;
  const shouldHide = displayInfo.hide_name_public ?? user.hide_name_public ?? false;
  const showRealName = displayInfo.show_real_name ?? user.show_real_name;
  const hasBadge = isVerified || isCreator || isAdmin;

  // Determine display name
  // Non-admin users never see real name (first_name + last_name)
  // Priority: viewer's private nickname → display_name → username
  //
  // `nickname` (or `custom_name`) is what the viewer has saved against
  // this user via the "Set Nickname" modal in collections — only the
  // viewer ever sees it. It always wins over the user's own display_name
  // when present, even on the admin pages, because the whole point is
  // "this is what I call this person."
  const isAdminPage = window.location.pathname.includes('/admin');
  const nickname = (typeof user.nickname === 'string' && user.nickname.trim())
    || (typeof user.custom_name === 'string' && user.custom_name.trim())
    || null;
  const getDisplayName = () => {
    if (nickname && !isAdminPage) {
      return nickname;
    }
    if (shouldHide && !isAdminPage) {
      return 'Anonymous User';
    }
    // Admin can see real name
    if (isAdminPage && user.name) {
      return user.name;
    }
    // Non-admin: display_name → username only
    return displayInfo.display_name
      || user.display_name
      || user.username
      || displayInfo.username
      || 'User';
  };

  const username = displayInfo.username || user.username;

  // Size configs
  const sizes = {
    xs: { text: 'text-xs', badge: 15 },
    sm: { text: 'text-sm', badge: 17 },
    md: { text: 'text-sm', badge: 18 },
    lg: { text: 'text-base', badge: 20 },
    xl: { text: 'text-lg', badge: 22 },
  };
  const s = sizes[size] || sizes.md;

  const handleClick = onClick ? (e) => { e.preventDefault(); onClick(user); } : undefined;

  return (
    <span
      className={`inline-flex items-center gap-1 ${onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
      onClick={handleClick}
    >
      <span className={`font-semibold truncate ${s.text} ${nameClassName}`}>
        {getDisplayName()}
      </span>

      {showBadge && hasBadge && (
        <VerifiedBadge
          type={isAdmin && isAdminPage ? 'admin' : 'creator'}
          size={s.badge}
        />
      )}

      {showUsername && username && (
        <span className="text-gray-400 font-normal ms-1" style={{ fontSize: s.badge - 2 }}>
          @{username}
        </span>
      )}
    </span>
  );
};

export default UserDisplay;
